import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useRef } from 'react';
import { useSelector } from 'react-redux';

import useMergeProps from '../../../../hooks/useMergeProps';
import { StateType } from '../../../../states';

import MissionDetailsAchievementProgress from './MissionDetailsAchievementProgress';
import selectMissionDetailsAchievementProgressContainerProps from './selectMissionDetailsAchievementProgressContainerProps';

const DEFAULT_MAX_COLUMNS = 5;

type MissionDetailsAchievementProgressContainerProps = {
  className?: string;
  missionId: number;
  achievementId: number;
};

const MissionDetailsAchievementProgressContainer = ({
  missionId,
  achievementId,
  ...ownProps
}: MissionDetailsAchievementProgressContainerProps) => {
  const selectorProps = useMemo(() => ({ missionId, achievementId }), [missionId, achievementId]);
  const selectedProps = useSelector((state: StateType) =>
    selectMissionDetailsAchievementProgressContainerProps(state, selectorProps),
  );

  const wrapperRef = useRef<HTMLElement>();

  const [maxColumns, setMaxColumns] = useState(DEFAULT_MAX_COLUMNS);

  useLayoutEffect(() => {
    const wrapperNode = wrapperRef.current;
    const cssStepDimLength = '1.5em'; // actual size of a progressStep

    if (wrapperNode) {
      const resizeEventCallback = () => {
        // the next block simulates an element of a step width and computes its width in px
        // (1.8em -> {?}px accordingly to the current font-size)
        const fakeElem = document.createElement('div');
        fakeElem.style.width = cssStepDimLength;
        fakeElem.style.position = 'absolute';
        fakeElem.style.background = 'transparent';
        fakeElem.style.pointerEvents = 'none';
        wrapperNode.appendChild(fakeElem);
        const castedCssStepDimLength = parseFloat(window.getComputedStyle(fakeElem).width);
        wrapperNode.removeChild(fakeElem);

        const { width } = wrapperNode.getBoundingClientRect();

        if (width && castedCssStepDimLength) {
          const nextMaxColumns = Math.max(Math.floor(width / (castedCssStepDimLength * 2 + 1)), 1);

          setMaxColumns(nextMaxColumns);
          // (castedCssStepDimLength * 2 - 1) because there is almost 2 steps in size between each step
          // (taking in account the separators)
        }
      };

      window.addEventListener('resize', () => {
        setTimeout(resizeEventCallback, 0);
      });

      resizeEventCallback();
      setTimeout(() => {
        resizeEventCallback();
      }, 0);

      return () => {
        window.removeEventListener('resize', resizeEventCallback);
      };
    }
  }, []);

  const enhancedProps = useMergeProps({ ownProps, selectedProps });

  return <MissionDetailsAchievementProgress {...enhancedProps} maxColumns={maxColumns} />;
};

export default MissionDetailsAchievementProgressContainer;
